class PdpVariantSelector extends HTMLElement {
  #controller;
  #fetchController;
  #variants = [];
  #displayRules = {};
  #groups = {};

  connectedCallback() {
    const variantsEl = this.querySelector('script[data-pdp-variants]');
    if (!variantsEl) return;

    this.#variants = JSON.parse(variantsEl.textContent);
    this._hiddenInput = this.closest('form')?.querySelector('input[name="id"]');
    this._selects = Array.from(this.querySelectorAll('.pdp-variant-selector__select'));
    this._sectionId = this.dataset.sectionId;
    this._productUrl = this.dataset.productUrl;
    this._section = this.closest('.shopify-section');

    const rulesEl = this.querySelector('script[data-pdp-display-rules]');
    if (rulesEl) {
      this.#displayRules = JSON.parse(rulesEl.textContent);
    }

    this.#resolveGroups();
    this.#filterOptions();
    this.#resolveDefaultVariant();

    this.#controller = new AbortController();
    for (const select of this._selects) {
      select.addEventListener('change', () => this.#handleChange(), {
        signal: this.#controller.signal,
      });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#fetchController?.abort();
  }

  // ================================================================
  // Display rules: group resolution
  // ================================================================

  /**
   * Parse display rules and resolve group winners.
   * Priority: p1 available > p2 available > p1 sold-out > p2 sold-out
   */
  #resolveGroups() {
    const groupData = {};

    for (const variant of this.#variants) {
      const rule = (this.#displayRules[String(variant.id)] || '').trim();
      if (!rule.includes(':')) continue;

      const colonIdx = rule.indexOf(':');
      const groupName = rule.slice(0, colonIdx);
      const priority = rule.slice(colonIdx + 1).toLowerCase();

      if (priority !== 'p1' && priority !== 'p2') continue;

      if (!groupData[groupName]) {
        groupData[groupName] = {};
      }
      groupData[groupName][priority] = {
        id: variant.id,
        available: variant.available,
      };
    }

    for (const [groupName, members] of Object.entries(groupData)) {
      const p1 = members.p1;
      const p2 = members.p2;
      let winner;

      if (p1?.available) {
        winner = p1;
      } else if (p2?.available) {
        winner = p2;
      } else if (p1) {
        winner = p1;
      } else if (p2) {
        winner = p2;
      }

      if (winner) {
        this.#groups[groupName] = {
          winnerId: winner.id,
          available: winner.available,
        };
      }
    }
  }

  /**
   * Get the effective rule for a variant.
   */
  #getRule(variantId) {
    const rule = (this.#displayRules[String(variantId)] || '').trim();

    if (rule.toLowerCase() === 'hide') return { action: 'hide' };

    if (rule.includes(':')) {
      const colonIdx = rule.indexOf(':');
      const groupName = rule.slice(0, colonIdx);
      const priority = rule.slice(colonIdx + 1).toLowerCase();
      return { action: 'group', groupName, priority };
    }

    return { action: 'show' };
  }

  // ================================================================
  // Option filtering: hide/relabel options based on display rules
  // ================================================================

  #filterOptions() {
    const hasRules = Object.values(this.#displayRules).some((r) => r.trim() !== '');
    if (!hasRules) return;

    for (const select of this._selects) {
      const optionIndex = Number(select.dataset.optionIndex);
      const toRemove = [];

      for (const option of select.options) {
        const optionValue = option.value;
        const visibility = this.#getOptionValueVisibility(optionValue, optionIndex);

        if (visibility.hidden) {
          toRemove.push(option);
        } else if (visibility.groupLabel) {
          option.textContent = visibility.groupLabel;
        }
      }

      for (const option of toRemove) {
        option.remove();
      }
    }
  }

  /**
   * Determine if an option value should be visible and how to label it.
   * Hidden if ALL variants with this value are hidden or p2.
   */
  #getOptionValueVisibility(value, optionIndex) {
    let visible = false;
    let groupLabel = '';

    for (const variant of this.#variants) {
      if (variant.options[optionIndex] !== value) continue;

      const { action, groupName, priority } = this.#getRule(variant.id);

      if (action === 'hide') continue;
      if (action === 'group' && priority === 'p2') continue;

      visible = true;
      if (action === 'group' && groupName) {
        const originalRule = (this.#displayRules[String(variant.id)] || '').trim();
        const colonIdx = originalRule.indexOf(':');
        groupLabel = originalRule.slice(0, colonIdx);
      }
    }

    return { hidden: !visible, groupLabel: groupLabel || undefined };
  }

  // ================================================================
  // Default variant resolution
  // ================================================================

  #resolveDefaultVariant() {
    const url = new URL(window.location.href);
    const urlVariantId = url.searchParams.get('variant');
    const serverRenderedId = this._hiddenInput.value;
    let resolved = null;

    if (urlVariantId) {
      const urlVariant = this.#findVariant(Number(urlVariantId));
      if (urlVariant) {
        resolved = this.#resolveVariant(urlVariant);
      }
    }

    if (!resolved) {
      resolved = this.#findFirstVisibleAvailable() || this.#findFirstVisible();
    }

    if (!resolved) return;

    const needsUpdate = String(resolved.id) !== serverRenderedId;

    this._hiddenInput.value = resolved.id;

    if (urlVariantId !== String(resolved.id)) {
      url.searchParams.set('variant', resolved.id);
      history.replaceState({}, '', url.toString());
    }

    if (needsUpdate) {
      this.#selectVariantInDropdowns(resolved);
      this.#fetchSection(resolved);
    }
  }

  /**
   * Resolve a variant through display rules: hidden returns null,
   * grouped returns the group winner.
   */
  #resolveVariant(variant) {
    const { action, groupName } = this.#getRule(variant.id);

    if (action === 'hide') return null;

    if (action === 'group' && groupName && this.#groups[groupName]) {
      return this.#findVariant(this.#groups[groupName].winnerId) || variant;
    }

    return variant;
  }

  #findFirstVisibleAvailable() {
    for (const variant of this.#variants) {
      if (!variant.available) continue;
      const resolved = this.#resolveVariant(variant);
      if (resolved?.available) return resolved;
    }
    return null;
  }

  #findFirstVisible() {
    for (const variant of this.#variants) {
      const { action, priority } = this.#getRule(variant.id);
      if (action === 'hide') continue;
      if (action === 'group' && priority === 'p2') continue;
      return this.#resolveVariant(variant);
    }
    return null;
  }

  #selectVariantInDropdowns(variant) {
    for (const [i, select] of this._selects.entries()) {
      const targetValue = variant.options[i];
      for (const option of select.options) {
        if (option.value === targetValue) {
          option.selected = true;
          break;
        }
      }
    }
  }

  // ================================================================
  // Variant change handling
  // ================================================================

  #findVariant(id) {
    return this.#variants.find((v) => v.id === id);
  }

  #handleChange() {
    const selectedOptions = this._selects.map((s) => s.value);
    let variant = this.#variants.find((v) =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );

    if (!variant) return;

    variant = this.#resolveVariant(variant);
    if (!variant) return;

    this._hiddenInput.value = variant.id;
    this.#updateUrl(variant);
    this.#fetchSection(variant);
  }

  #updateUrl(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());
  }

  // The Liquid template sorts media so the variant's featured media renders at
  // index 0, so this is usually a no-op. It acts as a fallback for edge cases
  // where the rendered order doesn't match — e.g. section caching returning
  // stale HTML for a different variant.

  #scrollToVariantMedia(variant) {
    if (!variant.featured_media) return;
    const scope = this._section || document;
    const carousel = scope.querySelector('pdp-media-carousel');
    if (!carousel) return;
    const mediaId = String(variant.featured_media.id);
    const slide = carousel.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    if (typeof carousel.goTo !== 'function') return;
    carousel.goTo(parseInt(slide.dataset.index, 10));
  }

  // ================================================================
  // Section rendering API
  // ================================================================

  async #fetchSection(variant) {
    if (!variant) return;

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const url = `${this._productUrl}?variant=${variant.id}&section_id=${this._sectionId}`;

    try {
      const response = await fetch(url, { signal: this.#fetchController.signal });
      if (!response.ok) return;

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      this.#updatePrice(html);
      this.#updateMedia(html);
      this.#scrollToVariantMedia(variant);
      this.#updateBuyButtons(html);
      this.#updateVariantSelects(html);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to fetch variant section:', error);
      }
    }
  }

  #updatePrice(html) {
    const scope = this._section || document;
    const newPrice = html.querySelector('[data-pdp-price]');
    const currentPrice = scope.querySelector('[data-pdp-price]');
    if (newPrice && currentPrice) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }
  }

  #updateMedia(html) {
    const scope = this._section || document;
    const newMedia = html.querySelector('[data-pdp-media]');
    const currentMedia = scope.querySelector('[data-pdp-media]');
    if (newMedia && currentMedia) {
      currentMedia.innerHTML = newMedia.innerHTML;
    }
  }

  #updateBuyButtons(html) {
    const scope = this._section || document;
    const newButtons = html.querySelector('[data-pdp-buy-buttons]');
    const currentButtons = scope.querySelector('[data-pdp-buy-buttons]');
    if (newButtons && currentButtons) {
      currentButtons.innerHTML = newButtons.innerHTML;
    }
  }

  #updateVariantSelects(html) {
    const newSelector = html.querySelector('pdp-variant-selector');
    if (!newSelector) return;

    const newSelects = newSelector.querySelectorAll('.pdp-variant-selector__select');
    for (const [i, select] of this._selects.entries()) {
      const newSelect = newSelects[i];
      if (!newSelect) continue;

      const newOptionsByValue = new Map();
      for (const newOpt of newSelect.options) {
        newOptionsByValue.set(newOpt.value, newOpt);
      }

      for (const option of select.options) {
        const newOption = newOptionsByValue.get(option.value);
        if (!newOption) continue;

        const wasUnavailable = option.dataset.unavailable === 'true';
        const isNowUnavailable = newOption.dataset.unavailable === 'true';

        if (wasUnavailable !== isNowUnavailable) {
          if (isNowUnavailable) {
            option.dataset.unavailable = 'true';
          } else {
            delete option.dataset.unavailable;
          }
          option.textContent = newOption.textContent;
        }
      }
    }
  }
}

if (!customElements.get('pdp-variant-selector')) {
  customElements.define('pdp-variant-selector', PdpVariantSelector);
}
